import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useAuthStore } from './auth';

export interface CartItem {
  listingId: string;
  marketHashName: string;
  weapon: string | null;
  skin: string | null;
  imageUrl: string | null;
  ourPriceCents: number;
  // Listing.source — 'DMARKET' items carry a 7-day Steam delivery hold;
  // 'P2P' (internal-seller) items are already in bot custody. Optional so
  // carts persisted before this field shipped still parse.
  source?: string | null;
}

const STORAGE_KEY = 'epic-cherry.cart.v1';

function loadInitial(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>(loadInitial());
  const open = ref(false);

  // Persist across reloads — small, single-user, localStorage is fine.
  watch(
    items,
    (next) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // quota / private mode — silent
      }
    },
    { deep: true },
  );

  // Auto-close + wipe the cart on logout. The popup would otherwise stick
  // around after the auth header switches to "log in / sign up", which
  // confused users (GGWP-543). Also wiping the items so the next user on
  // the same browser doesn't see the previous user's selection.
  const auth = useAuthStore();
  watch(
    () => auth.user,
    (next, prev) => {
      // Fires only on the actual transition logged-in → logged-out;
      // the initial null → null on first load is a no-op.
      if (prev && !next) {
        open.value = false;
        items.value = [];
      }
    },
  );

  const totalCents = computed(() =>
    items.value.reduce((sum, i) => sum + i.ourPriceCents, 0),
  );
  const count = computed(() => items.value.length);
  const isEmpty = computed(() => items.value.length === 0);

  function add(item: CartItem) {
    if (items.value.some((i) => i.listingId === item.listingId)) return;
    items.value = [...items.value, item];
    open.value = true;
  }

  function remove(listingId: string) {
    items.value = items.value.filter((i) => i.listingId !== listingId);
  }

  function clear() {
    items.value = [];
  }

  function toggle() {
    open.value = !open.value;
  }

  function close() {
    open.value = false;
  }

  return { items, open, totalCents, count, isEmpty, add, remove, clear, toggle, close };
});
